import { apiClient } from "../lib/api-client";
import {
  Project,
  PaginatedResponse,
  CompositionSettings,
} from "../types/api.types";

export interface CreateProjectData {
  title: string;
}

export interface UpdateProjectData {
  title?: string;
  thumbnailUrl?: string;
  previewUrl?: string;
  compositionSettings?: CompositionSettings;
}

export const projectService = {
  async getProjects(page = 1, limit = 10, search = ""): Promise<PaginatedResponse<Project>> {
    const query = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) query.append("search", search);

    const response = await apiClient.get<{
      projects: Project[];
      pagination: any;
    }>(`/projects?${query.toString()}`);
    if (response.success && response.data) {
      return {
        items: response.data.projects,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.message || "Failed to fetch projects");
  },

  async getProject(projectId: string): Promise<Project> {
    const response = await apiClient.get<{ project: Project }>(
      `/projects/${projectId}`
    );
    if (response.success && response.data) {
      return response.data.project;
    }
    throw new Error(response.message || "Failed to fetch project");
  },

  async createProject(data: CreateProjectData): Promise<Project> {
    const response = await apiClient.post<{ project: Project }>(
      "/projects",
      data
    );
    if (response.success && response.data) {
      return response.data.project;
    }
    throw new Error(response.message || "Failed to create project");
  },

  async updateProject(
    projectId: string,
    data: UpdateProjectData
  ): Promise<Project> {
    const response = await apiClient.put<{ project: Project }>(
      `/projects/${projectId}`,
      data
    );
    if (response.success && response.data) {
      return response.data.project;
    }
    throw new Error(response.message || "Failed to update project");
  },

  async autosaveProject(
    projectId: string,
    compositionSettings: CompositionSettings
  ): Promise<void> {
    const response = await apiClient.post(`/projects/${projectId}/autosave`, {
      compositionSettings,
    });
    if (!response.success) {
      throw new Error(response.message || "Autosave failed");
    }
  },

  async deleteProject(projectId: string): Promise<void> {
    const response = await apiClient.delete(`/projects/${projectId}`);
    if (!response.success) {
      throw new Error(response.message || "Failed to delete project");
    }
  },
};
